/**
 * 🔒 P2 보안 개선: 세션 타임아웃 훅
 *
 * 사용자 비활성 상태 감지 후 자동 로그아웃
 * - 기본 타임아웃: 30분
 * - 경고 시간: 타임아웃 5분 전
 * - 활동 감지: 마우스, 키보드, 스크롤, 터치
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import { logger } from '../utils/logger.ts';

// 세션 타임아웃 설정
const SESSION_CONFIG = {
  timeout: 30 * 60 * 1000,        // 30분 (밀리초)
  warningBefore: 5 * 60 * 1000,   // 5분 전 경고 (밀리초)
  checkInterval: 60 * 1000,       // 1분마다 체크 (밀리초)
  storageKey: 'lastActivityTime'
};

// 감지할 사용자 활동 이벤트
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click'
];

export interface SessionTimeoutState {
  isWarningVisible: boolean;
  remainingTime: number | null;
  extendSession: () => void;
}

export function useSessionTimeout(): SessionTimeoutState {
  const { user, logout } = useAuth();
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [remainingTime, setRemainingTime] = useState<number | null>(null);

  const lastActivityRef = useRef<number>(Date.now());
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const logoutTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * 마지막 활동 시간 업데이트
   */
  const updateLastActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;

    // localStorage에도 저장 (탭 간 동기화)
    try {
      localStorage.setItem(SESSION_CONFIG.storageKey, now.toString());
    } catch {
      // localStorage 사용 불가 시 무시
    }

    // 경고 상태 초기화
    if (isWarningVisible) {
      setIsWarningVisible(false);
      setRemainingTime(null);
      logger.debug('[SessionTimeout] Session extended by user activity');
    }
  }, [isWarningVisible]);

  /**
   * 세션 연장 (경고 다이얼로그에서 호출)
   */
  const extendSession = useCallback(() => {
    updateLastActivity();
    logger.debug('[SessionTimeout] Session manually extended');
  }, [updateLastActivity]);

  /**
   * 로그아웃 실행
   */
  const performLogout = useCallback(async () => {
    logger.warn('[SessionTimeout] Auto-logout due to inactivity');
    setIsWarningVisible(false);
    setRemainingTime(null);

    try {
      await logout();
      // 로그아웃 후 로그인 페이지로 리다이렉트는 AuthContext에서 처리
    } catch (error) {
      logger.error('[SessionTimeout] Logout error:', error instanceof Error ? error : undefined);
    }
  }, [logout]);

  /**
   * 타임아웃 체크
   */
  const checkTimeout = useCallback(() => {
    if (!user) return;

    const now = Date.now();

    // localStorage에서 마지막 활동 시간 확인 (탭 간 동기화)
    try {
      const storedTime = localStorage.getItem(SESSION_CONFIG.storageKey);
      if (storedTime) {
        const storedTimeNum = parseInt(storedTime, 10);
        if (storedTimeNum > lastActivityRef.current) {
          lastActivityRef.current = storedTimeNum;
        }
      }
    } catch {
      // localStorage 사용 불가 시 무시
    }

    const timeSinceLastActivity = now - lastActivityRef.current;
    const timeUntilTimeout = SESSION_CONFIG.timeout - timeSinceLastActivity;

    // 타임아웃 도달
    if (timeUntilTimeout <= 0) {
      performLogout();
      return;
    }

    // 경고 시간 도달
    if (timeUntilTimeout <= SESSION_CONFIG.warningBefore && !isWarningVisible) {
      setIsWarningVisible(true);
      setRemainingTime(Math.ceil(timeUntilTimeout / 1000));
      logger.debug('[SessionTimeout] Warning: session about to expire', {
        context: { remainingSeconds: Math.ceil(timeUntilTimeout / 1000) }
      });
    }

    // 남은 시간 업데이트 (경고 중일 때)
    if (isWarningVisible) {
      setRemainingTime(Math.ceil(timeUntilTimeout / 1000));
    }
  }, [user, isWarningVisible, performLogout]);

  /**
   * 이벤트 리스너 설정
   */
  useEffect(() => {
    if (!user) {
      // 로그아웃 상태면 정리
      setIsWarningVisible(false);
      setRemainingTime(null);
      return;
    }

    // 초기 활동 시간 설정
    updateLastActivity();

    // 활동 이벤트 리스너 등록
    const handleActivity = () => {
      updateLastActivity();
    };

    // 이벤트 리스너 추가 (throttle 적용)
    let throttleTimer: NodeJS.Timeout | null = null;
    const throttledHandler = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        handleActivity();
        throttleTimer = null;
      }, 1000); // 1초 쓰로틀링
    };

    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, throttledHandler, { passive: true });
    });

    // 주기적 타임아웃 체크
    checkIntervalRef.current = setInterval(checkTimeout, SESSION_CONFIG.checkInterval);

    // 초기 체크
    checkTimeout();

    logger.debug('[SessionTimeout] Session monitoring started', {
      context: { timeout: SESSION_CONFIG.timeout / 60000 + ' minutes' }
    });

    return () => {
      // 이벤트 리스너 제거
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, throttledHandler);
      });

      // 타이머 정리
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
      }
      if (throttleTimer) {
        clearTimeout(throttleTimer);
      }

      logger.debug('[SessionTimeout] Session monitoring stopped');
    };
  }, [user, updateLastActivity, checkTimeout]);

  return {
    isWarningVisible,
    remainingTime,
    extendSession
  };
}

export default useSessionTimeout;
